import { type GitlabProvisioningTopLevelGroup } from 'network/httpClients/GitlabClient/api.schema';
import { SELECTION_ACTIONS } from './consts';

/** GitLab host, derived from the connect URL: gitlab.com vs a self-managed instance. */
export const GITLAB_V2_HOST = {
  CLOUD: 'cloud',
  SELF_MANAGED: 'self-managed',
} as const;

export type GitlabV2Host = (typeof GITLAB_V2_HOST)[keyof typeof GITLAB_V2_HOST];

/**
 * The connect capability, resolved by the BE at connect and stashed in context
 * for the later steps. Mirrors `GitlabProvisioningConnectResponse` but with the
 * wire nulls normalized to `undefined` (FE convention: no null in state).
 */
export type GitlabV2ConnectResult = {
  hostType: GitlabV2Host;
  baseUrl: string;
  edition: 'ee' | 'ce';
  licensePlan: 'ultimate' | 'premium' | 'free' | undefined;
  botType: 'service_account' | 'user_impersonation';
  topLevelGroup: GitlabProvisioningTopLevelGroup | undefined;
  /** BE signal that the top-level-group selection page is needed before provisioning. */
  needsGroupSelection: boolean;
};

export type ConnectFormData = {
  gitlabUrl: string;
  accessToken: string;
};

/** Select-step tree selection state. Group vs project granularity is chosen by
 * `groupWebhooksSupported`; both sets exist so the reducer stays mode-agnostic. */
export type GitlabV2SelectionState = {
  selectedGroupIds: Set<number>;
  selectedProjectIds: Set<number>;
};

export type GitlabV2SelectionAction =
  | { type: typeof SELECTION_ACTIONS.TOGGLE_GROUP; id: number }
  | { type: typeof SELECTION_ACTIONS.TOGGLE_PROJECT; id: number }
  | {
      type: typeof SELECTION_ACTIONS.PRESELECT;
      groupIds: number[];
      projectIds: number[];
    }
  | {
      type: typeof SELECTION_ACTIONS.DESELECT;
      groupIds: number[];
      projectIds: number[];
    }
  | { type: typeof SELECTION_ACTIONS.RESET };

/** The computed install payload handed from Select → Install via context. */
export type GitlabV2InstallSelection = {
  groupIds: number[];
  projectIds: number[];
};

/**
 * One top-level group's slice of a cloud multi-group selection: the group to provision
 * an installation for, plus the scopes to install under it. `add` matches the `/manage`
 * add shape (first install is add-only).
 */
export type GitlabV2GroupInstallPartition = {
  topLevelGroupId: number;
  groupPath: string;
  add: { groupIds: number[]; projectIds: number[] };
};

/**
 * Manage add/remove deltas — the `/manage` request body's coverage edit. Computed
 * from the baseline (pre-ticked `own_hook` scopes) vs. the current selection.
 */
export type GitlabV2ManageDeltas = {
  add: { groupIds: number[]; projectIds: number[] };
  remove: { groupIds: number[]; projectIds: number[] };
};

export type GitlabInstallationV2ContextValue = {
  /** The GitLab URL the user is connecting; host (cloud vs self-managed) is derived from it. */
  gitlabUrl: string;
  setGitlabUrl: (gitlabUrl: string) => void;
  /**
   * The pasted elevation PAT, held in memory for the wizard's lifetime and
   * re-sent on each stateless call (provision / select / install). Never
   * persisted anywhere; cleared when the dialog unmounts. Undefined until
   * Connect succeeds.
   */
  accessToken: string | undefined;
  setAccessToken: (accessToken: string | undefined) => void;
  /** Capability resolved at connect; undefined until Connect succeeds. */
  connectResult: GitlabV2ConnectResult | undefined;
  setConnectResult: (connectResult: GitlabV2ConnectResult | undefined) => void;
  /**
   * Cloud host connected without a group (`hostType === 'cloud' && needsGroupSelection`):
   * provisioning is deferred to Install, where it fans out one install per selected
   * top-level group. False for self-managed and legacy single-group cloud (which
   * provision on Connect). Set at Connect success.
   */
  isMultiGroupCloud: boolean;
  setIsMultiGroupCloud: (isMultiGroupCloud: boolean) => void;
  /** git_installations id, set once Connect's Next provisions the bot; carried into Select/Install. */
  installationId: string | undefined;
  setInstallationId: (installationId: string | undefined) => void;
  /** Selection computed at Select's Next, consumed by the Install step. */
  installSelection: GitlabV2InstallSelection | undefined;
  setInstallSelection: (
    installSelection: GitlabV2InstallSelection | undefined,
  ) => void;
  /** id→path maps carried from Select, so the Install step can label results
   * whose scope arrives as a bare id while still queued/in_progress. */
  scopePaths: GitlabV2ScopePaths;
  setScopePaths: (scopePaths: GitlabV2ScopePaths) => void;
};

/** Loaded id→path maps for labeling result rows (groups + projects). */
export type GitlabV2ScopePaths = {
  groupPathsById: Map<number, string>;
  projectPathsById: Map<number, string>;
  /** Loaded group/project id → its owning top-level group id, for multi-group
   * selection partitioning (BE stamps `topLevelGroupId` on every select item). */
  topLevelGroupIdByGroupId: Map<number, number>;
  topLevelGroupIdByProjectId: Map<number, number>;
};
